import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import { getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import CustomGrid from 'components/grids/CustomGrid';
import {
  clearFinalReportPending,
  getBreakerMarginRatioList,
  getMachineMarginRatioList,
  getRptOpinions,
  isAiDraftPending,
  isFinalReportPending,
  requestFinalReport,
  saveRptOpinions,
} from 'apis/ut/UtMatrixComparisonReportList';
import useSessionStore from 'stores/useSessionStore';
import { Code } from 'models/common/CommonCode';
import {
  ANLS_OPIN_TP_CD_GROUP,
  AtcDivsCd,
  RptOpinion,
} from 'models/ut/UtMatrixComparisonReportList';

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  alignItems: 'stretch',
};

/** 검토 의견 칸을 숨겼을 때 — 분석 본문이 한 줄을 다 쓴다 */
const singleColRowStyle: React.CSSProperties = {
  ...rowStyle,
  gridTemplateColumns: '1fr',
};

/** 행 안의 한 칸 — minWidth 0으로 긴 문장에서 칸이 밀리지 않게 한다 */
const colStyle: React.CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

/**
 * 차단기 여유율 분석(A-0001) — 분석 결과(좌) / Machine별(우) 그리드를 나란히 놓는다.
 *
 * 직접 만든 <table> 대신 공용 CustomGrid(Wijmo)를 쓴다 — 헤더 고정을 그리드가 알아서
 * 처리하므로 2단 헤더의 sticky top을 px로 계산할 필요가 없고, 정렬/툴팁/복사도 따라온다.
 */
const marginTableRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  alignItems: 'start',
};

const marginTableColStyle: React.CSSProperties = {
  minWidth: 0,
};

/**
 * 두 그리드의 본문 높이. 헤더가 2단(그룹 헤더 + 소헤더)으로 양쪽이 같아서
 * 이 값만 맞추면 전체 높이도 같아진다(CustomGrid가 height + 헤더높이로 그린다).
 */
const MARGIN_GRID_H = 240;

/**
 * 차단기 여유율 분석 항목코드 — 이 항목만 본문 대신 표 두 개를 그린다(전기 전용).
 * 표가 각자 제목을 달고 있어 본문 라벨도 붙이지 않는다.
 */
const BRKE_MARGIN_CD = 'A-0001';

/** 좌: 공정·설비군별 차단기 여유율 */
const BRKE_MARGIN_LAYOUT = [
  { binding: 'PRDN_PROC_NM', header: '공정', minWidth: 70, width: 120 },
  { binding: 'EQCL_NM', header: '설비군', minWidth: 80, width: '*' },
  {
    header: '차단기 여유율',
    columns: [
      { binding: 'SHORT_CNT', header: '부족', minWidth: 46, width: 62, align: 'center' },
      { binding: 'OK_CNT', header: '적정', minWidth: 46, width: 62, align: 'center' },
      { binding: 'OVER_CNT', header: '과다', minWidth: 46, width: 62, align: 'center' },
      { binding: 'TOTAL_CNT', header: '합계', minWidth: 46, width: 62, align: 'center' },
    ],
  },
];

/** 우: Machine별 차단기 여유율 (기존 MachineMarginModal의 레이아웃과 동일) */
const MACH_MARGIN_LAYOUT = [
  { binding: 'EQCL_NM', header: '설비군', minWidth: 70, width: 150 },
  { binding: 'MACHINE_NM', header: 'Machine', minWidth: 80, width: '*' },
  {
    header: '차단기 여유율',
    columns: [
      { binding: 'SHORT_CNT', header: '부족', minWidth: 46, width: 62, align: 'center' },
      { binding: 'OK_CNT', header: '적정', minWidth: 46, width: 62, align: 'center' },
      { binding: 'OVER_CNT', header: '과다', minWidth: 46, width: 62, align: 'center' },
      { binding: 'TOTAL_CNT', header: '합계', minWidth: 46, width: 62, align: 'center' },
    ],
  },
];

/**
 * 좌측 그리드의 합계 행 배경 구분 — 두 단계를 다르게 칠한다.
 * - 공정·설비군이 모두 '전체' = 총계 → sumTotal
 * - 공정은 특정 값이고 설비군만 '전체' = 그 공정의 소계 → rowTotal
 *
 * sumTotal/rowTotal은 wijmo 그리드 공용 클래스다
 * (ui/css/multiSelect.css — 기계/전기 요약 화면의 Total Sum / Sub Sum과 같은 색).
 */
const brkeMarginItemFormatter = (panel, row, col, cell) => {
  if (CellType.Cell !== panel.cellType) return;
  const item = panel.rows[row]?.dataItem;
  if (item?.EQCL_NM !== '전체') return;
  addClass(cell, item?.PRDN_PROC_NM === '전체' ? 'sumTotal' : 'rowTotal');
};

const marginTableCaptionStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted)',
  margin: '0 0 4px',
};

const itemStyle: React.CSSProperties = {
  border: '1px solid #dde0df',
  borderRadius: 3,
  background: '#fff',
  padding: '12px 14px',
  marginBottom: 10,
};

const boxStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 60,
  border: '1px solid #cfe6ec',
  background: '#f4fbfc',
  borderRadius: 3,
  padding: '10px 12px',
  fontSize: 12,
  lineHeight: 1.7,
  color: '#333',
  whiteSpace: 'pre-wrap',
};

const emptyBoxStyle: React.CSSProperties = {
  ...boxStyle,
  background: '#fafafa',
  borderColor: '#e5e5e5',
  color: '#9a9a9a',
  fontStyle: 'italic',
};

const textareaStyle: React.CSSProperties = {
  flex: 1,
  width: '100%',
  minHeight: 60,
  padding: '8px 10px',
  border: '1px solid #b9bcbb',
  borderRadius: 3,
  fontSize: 12,
  lineHeight: 1.7,
  fontFamily: 'inherit',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const itemTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#135678',
  margin: '0 0 8px',
};

/** 제목 + 우측 액션 버튼을 한 줄에 놓는다 (renderItemAction이 없으면 제목만 있는 것과 같다) */
const itemTitleRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--text-muted)',
  margin: '0 0 4px',
};

/**
 * 저장 버튼 — 이 컴포넌트는 기계(pages/ut/styles.ts)와 전기(elec/styles.ts) 두 스코프에서
 * 공용으로 쓰이는데 두 스타일시트의 버튼 클래스명이 서로 달라(.btn-primary / .btn-dark)
 * className을 쓰면 한쪽에서 스타일이 통째로 빠진다. 그래서 인라인으로 고정한다
 * (이 파일의 다른 요소들도 모두 인라인 스타일이다).
 */
const saveButtonStyle: React.CSSProperties = {
  height: 32,
  padding: '6px 14px',
  border: 'none',
  borderRadius: 2,
  background: '#2d9bb2',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

const saveButtonDisabledStyle: React.CSSProperties = {
  ...saveButtonStyle,
  opacity: 0.4,
  cursor: 'default',
};

const mutedStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)' };

const errorStyle: React.CSSProperties = { fontSize: 12, color: '#b32542' };

/** 생성 중 표시용 회전 아이콘 — 키프레임은 아래 SPINNER_KEYFRAMES로 주입한다 */
const spinnerStyle: React.CSSProperties = {
  display: 'inline-block',
  width: 10,
  height: 10,
  marginRight: 6,
  border: '2px solid #d5e8ee',
  borderTopColor: '#2d9bb2',
  borderRadius: '50%',
  animation: 'utAiSpin 0.8s linear infinite',
  verticalAlign: 'middle',
};

const SPINNER_KEYFRAMES = '@keyframes utAiSpin { to { transform: rotate(360deg); } }';

interface Props {
  rptId: string;
  atcDivsCd: AtcDivsCd;
  /**
   * 왼쪽 칸 라벨. 자동 생성 주체가 도메인마다 달라서 문구만 바꾼다.
   * - 기계: AI가 생성 → 'AI 분석'
   * - 전기: 규칙 기반 생성 → '분석 결과'
   *   (ref/README.md §4 "종합 분석 3항목 자동 생성(refreshElecOpinions)")
   *
   * 사용자가 고칠 수 있는 칸이라 '(자동)'은 붙이지 않는다.
   */
  analysisLabel?: string;
  /**
   * AI 생성 진행 상태(스피너 + 폴링) 사용 여부 (기본 true).
   * 전기는 AI를 쓰지 않으므로 false로 끈다.
   */
  useAiProgress?: boolean;
  /**
   * 왼쪽 "자동 생성" 칸을 사용자가 직접 수정할 수 있게 할지 (기본 false).
   * 전기는 규칙 기반으로 만든 문구를, 기계는 AI가 만든 문구를 사람이 다듬어 쓰므로 둘 다 연다.
   */
  editableAnalysis?: boolean;
  /**
   * 왼쪽 분석 본문을 읽고 쓸 컬럼 (기본 AI_ANS_CTN).
   *
   * - 기계: AI가 AI_ANS_CTN을 채우므로 그대로 둔다.
   * - 전기: 규칙 기반이라 AI 칸을 쓸 이유가 없어 ANLS_RLT_CTN에 넣는다
   *   (기계 UT 항목별 분석결과 ⑥과 같은 자리).
   *
   * 표시할 때는 지정한 쪽을 우선하고 비어 있으면 다른 쪽으로 넘어간다 — 컬럼을 옮기기 전에
   * 저장된 리포트도 그대로 보이게 하기 위함이다.
   */
  analysisField?: 'aiAnsCtn' | 'anlsRltCtn';
  /**
   * `analysisField`의 **예외 항목** — 이 코드들만 `AI_ANS_CTN`을 쓴다.
   *
   * 전기처럼 한 구분 안에 생성 주체가 섞여 있을 때 쓴다. 전기 A-0001~0003은 규칙 기반
   * (`ANLS_RLT_CTN`)이고 A-0004 "전력 사용량 종합분석"만 AI(`AI_ANS_CTN`)다.
   * 컬럼을 하나로 통일하면 재생성 때 어느 쪽이 덮어써야 하는지 판단할 수 없다.
   *
   * 진행 상태 판단(AI 생성 중 표시·폴링)도 이 항목들만 본다 — 규칙 기반 항목은 리포트
   * 생성 시 즉시 채워지므로, 그것까지 세면 AI 초안이 안 왔는데도 "비어 있지 않다"가 되어
   * 진행 표시가 뜨지 않는다(기계의 UT 9종을 shownCodes로 걸러내는 것과 같은 이유).
   */
  aiFieldCodes?: string[];
  /**
   * 그릴 항목을 고른다 — **한 구분(ATC_DIVS_CD)을 여러 섹션으로 쪼갤 때** 쓴다.
   *
   * `onlyCodes`: 이 코드만 그린다. `excludeCodes`: 이 코드를 뺀다. 둘 다 없으면 전부 그린다.
   *
   * 전기가 이 경우다 — 규칙 기반 3항목과 AI 1항목을 각각 별도 SectionBox로 나눈다
   * (`excludeCodes={AI코드}` / `onlyCodes={AI코드}`).
   *
   * ⚠ 규칙 기반 쪽은 `onlyCodes`로 A-0001~0003을 나열하지 말고 **`excludeCodes`로 AI 코드만
   *   빼야 한다.** 항목 목록의 출처는 공통코드 하나라는 원칙 때문이다 — 나열해 두면 나중에
   *   규칙 기반 항목이 공통코드에 추가돼도 화면에 나타나지 않는다.
   */
  onlyCodes?: string[];
  excludeCodes?: string[];
  /**
   * 오른쪽 "검토 의견"(OPIN_CTN) 칸 표시 여부 (기본 true).
   * 전기는 패널 하단에 전체 종합의견(RPT_M.RMK) 입력칸이, 기계는 ⑦ 하단에 종합의견 입력칸이
   * 따로 있어 양쪽 다 항목별 의견칸을 두지 않는다.
   *
   * ⚠ 이 칸을 끄면 최종 리포트 AI 호출(requestFinalReport)도 함께 꺼진다 — save() 참고.
   */
  showOpinion?: boolean;
  /** 최종 리포트 AI 호출에 실어 보낼 분석명 (없으면 빈 값으로 나간다) */
  rptNm?: string;
  /**
   * 자체 저장 버튼을 감춘다 (기본 false).
   * 탭 하단의 저장을 버튼 하나로 모을 때 부모가 ref로 save()를 직접 호출한다.
   */
  hideSaveButton?: boolean;
  /**
   * 항목 제목줄 오른쪽에 붙일 액션 (항목코드별). 해당 코드에 줄 게 없으면 null을 반환하면 된다.
   * 항목 목록이 공통코드로 정해지므로, 특정 항목에만 버튼을 다는 건 이 슬롯으로 처리한다.
   *
   * (전기 A-0001의 "Machine 여유율" 팝업 버튼이 이 슬롯의 첫 사용처였으나, 26.09.23에 표를
   *  본문 오른쪽으로 옮기면서 버튼이 사라져 현재 쓰는 곳은 없다.)
   */
  renderItemAction?: (anlsOpinTpCd: string) => ReactNode;
  /**
   * 항목 제목 덮어쓰기 (항목코드 → 표시명). 공통코드명(cmnCdNm) 대신 화면에만 다른 이름을
   * 쓰고 싶을 때 넘긴다 — 저장되는 코드값은 그대로다.
   */
  itemTitles?: Record<string, string>;
}

/** 부모가 저장 버튼을 하나로 합칠 때 쓰는 핸들 */
export interface OpinionSaveHandle {
  save: () => Promise<void>;
}

/** AI 생성이 끝났는지 다시 확인하는 주기 */
const POLL_INTERVAL_MS = 10_000;

/**
 * 이 구분의 AI 본문 상태를 하나의 문자열로 요약한다.
 * 최종 리포트 반영 전/후를 비교해 "갱신됐는지"를 판단하는 용도.
 */
const aiSignature = (rows: RptOpinion[], atcDivsCd: AtcDivsCd): string =>
  rows
    .filter((o) => o.atcDivsCd === atcDivsCd)
    .map((o) => `${o.anlsOpinTpCd}:${o.aiAnsCtn ?? ''}`)
    .sort()
    .join('|');

/**
 * ⑦ 종합 의견 — TB_EELMB_UTM_RPT_OPIN_M을 ATC_DIVS_CD(기계/전기) 기준으로 읽고 쓴다.
 *
 * 항목 목록(=블록 개수)은 하드코딩하지 않고 공통코드 ANLS_OPIN_TP_CD에서 가져온다.
 * 상세코드의 OPT_VAL_CTN1이 ATC_DIVS_CD(MECH/ELEC)이므로 도메인별로 항목 수가 다르다
 * (현재 기계 2건: Air 계열 분석/권고조치사항, 전기 3건: 차단기 여유율 분석/
 * Area별 전압 통합화 제안/훅업 포인트 간소화).
 *
 * 항목 하나가 "타이틀 + (AI 분석 / 검토 의견)" 좌우 2칸으로 렌더된다 —
 * ref/html/ut_matrix_rpt_demo.html의 종합 의견 블록(1193~1204줄)과 같은 형태다.
 * 왼쪽은 자동 생성 본문(AI_ANS_CTN, 없으면 ANLS_RLT_CTN)이고 오른쪽은 사용자가
 * 입력하는 최종 의견(OPIN_CTN)이다. 생성 주체가 도메인마다 달라(기계=AI, 전기=규칙 기반)
 * 왼쪽 칸 라벨만 analysisLabel로 바꾼다 — 칸 구성은 양쪽 동일하다.
 *
 * 현재 기계·전기 두 화면 모두 오른쪽 검토 의견 칸을 끄고(showOpinion=false) 왼쪽 본문을
 * 직접 고치는 형태로 쓴다(editableAnalysis) — 항목별 의견 대신 각 화면 하단의 종합의견
 * 입력칸을 쓰기로 해서다. 2칸 구성은 프롭 기본값으로 남겨 둔다.
 *
 * ⚠ 이전에는 좌우가 "(보고서 반영전)=ANLS_RLT_CTN / (보고서 반영후)=AI_ANS_CTN"
 *   읽기 전용 2칸이었다(BE-REQUEST-ai-report-opinion-v4.md §1). HTML 기준으로
 *   개편하면서 오른쪽 칸을 사용자 입력으로 바꿨고, 반영전 텍스트는 AI 본문이 아직
 *   없을 때의 대체 표시로만 쓴다.
 */
const AnalysisOpinionSection = forwardRef<OpinionSaveHandle, Props>(function AnalysisOpinionSection(
  {
    rptId,
    atcDivsCd,
    analysisLabel = 'AI 분석',
    useAiProgress = true,
    editableAnalysis = false,
    analysisField = 'aiAnsCtn',
    aiFieldCodes,
    onlyCodes,
    excludeCodes,
    showOpinion = true,
    rptNm = '',
    hideSaveButton = false,
    renderItemAction,
    itemTitles,
  },
  ref
) {
  const userId = useSessionStore((s) => s.userId);

  /** 이 인스턴스가 그릴 항목인지 (onlyCodes/excludeCodes) */
  const isShown = (cd: string): boolean => {
    if (!cd) return false;
    if (onlyCodes?.length && !onlyCodes.includes(cd)) return false;
    if (excludeCodes?.length && excludeCodes.includes(cd)) return false;
    return true;
  };

  /** 차단기 여유율 표(A-0001 전용)를 조회할 인스턴스인지 */
  const needsBrkeMargin = atcDivsCd === 'ELEC' && isShown(BRKE_MARGIN_CD);

  /** 이 항목이 쓰는 본문 컬럼 — aiFieldCodes에 든 코드만 AI 칸을 쓴다 */
  const fieldOf = (cd: string): 'aiAnsCtn' | 'anlsRltCtn' =>
    aiFieldCodes?.includes(cd) ? 'aiAnsCtn' : analysisField;

  /**
   * 왼쪽 칸 라벨 — **AI 항목은 비운다.**
   *
   * 생성 주체는 제목의 `(AI)`로 드러난다(itemTitles). 라벨에 또 'AI 분석'을 쓰면 같은 말이
   * 제목 바로 아래 한 번 더 나온다. 기계 탭이 쓰는 표기 방식과 같다.
   */
  const labelOf = (cd: string): string => (aiFieldCodes?.includes(cd) ? '' : analysisLabel);

  /** 분석 본문 읽기 — 지정 컬럼 우선, 비어 있으면 반대쪽 (컬럼 이전 전 데이터 호환) */
  const analysisTextOf = (o?: RptOpinion): string =>
    (fieldOf(o?.anlsOpinTpCd ?? '') === 'anlsRltCtn'
      ? o?.anlsRltCtn || o?.aiAnsCtn
      : o?.aiAnsCtn || o?.anlsRltCtn) || '';

  const [codes, setCodes] = useState<Code[]>([]);
  /** 이 리포트의 전체 의견 행 — 저장 시 손실 없이 되돌려 보내기 위해 통째로 들고 있는다 */
  const [opinions, setOpinions] = useState<RptOpinion[]>([]);
  const [brkeMarginRatios, setBrkeMarginRatios] = useState<any[]>([]); // 차단기 여유율(설비군별)
  const [machMarginRatios, setMachMarginRatios] = useState<any[]>([]); // 차단기 여유율(Machine별)
  /** 사용자가 편집 중인 최종 의견 (anlsOpinTpCd → OPIN_CTN) */
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  /** 사용자가 편집 중인 분석 본문 (anlsOpinTpCd → AI_ANS_CTN). editableAnalysis일 때만 쓴다 */
  const [analysisDrafts, setAnalysisDrafts] = useState<Record<string, string>>({});
  /**
   * 사용자가 손댄 분석 본문 항목 — 폴링 재조회가 덮어써도 되는지 판단하는 기준.
   *
   * "값이 아직 없으면 채운다"로 판단하면 안 된다. 리포트 생성 직후에는 내용이 빈 행이
   * 먼저 조회될 수 있는데, 그때 draft가 ''로 확정돼 버리면 뒤늦게 도착한 AI 초안이
   * 화면에 영영 안 들어온다(기계 = AI 생성 + 폴링).
   */
  const touchedAnalysisRef = useRef<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  /** AI 초안을 요청해 두고 아직 내용이 안 들어온 상태 */
  const [generating, setGenerating] = useState(false);
  /** 재조회 트리거 — 값이 바뀌면 아래 조회 effect가 다시 돈다 */
  const [pollCount, setPollCount] = useState(0);
  /** 최종 리포트 AI를 태워두고 BE가 AI_ANS_CTN에 반영하기를 기다리는 중 */
  const [finalPending, setFinalPending] = useState(false);
  /** 요청 시점의 AI 본문 스냅샷 — 이 값이 바뀌면 반영이 끝난 것으로 본다 */
  const finalSnapshotRef = useRef<string>('');

  useEffect(() => {
    if (!rptId) return;
    let alive = true;
    // 폴링으로 다시 부를 때는 "불러오는 중" 문구로 화면이 깜빡이지 않게 한다
    if (pollCount === 0) setLoading(true);
    setError('');
    // 항목 목록(공통코드)과 저장된 내용을 함께 받아온다.
    // 내용 조회가 실패해도 항목 목록은 보여줘야 하므로 여기서 함께 실패시키지 않는다.
    Promise.all([
      getCommonCodeNamesCondition({ cmnGrCd: ANLS_OPIN_TP_CD_GROUP, optValCtn1: atcDivsCd }),
      getRptOpinions(rptId).catch(() => [] as RptOpinion[]),
      // 전기인 경우 차단기 여유율 조회 — A-0001에 설비군별/Machine별 표를 나란히 보여준다.
      // A-0001을 그리지 않는 인스턴스(섹션 분리 시 AI 쪽)는 이 표가 필요 없어 부르지 않는다.
      needsBrkeMargin ? getBreakerMarginRatioList(rptId) : Promise.resolve([]),
      needsBrkeMargin ? getMachineMarginRatioList(rptId) : Promise.resolve([]),
    ])
      .then(([codeList, saved, brke, mach]) => {
        if (!alive) return;
        setCodes(codeList ?? []);
        setOpinions(saved);
        setBrkeMarginRatios(brke);
        setMachMarginRatios(mach);
        // 사용자가 편집 중이던 내용을 덮지 않도록, 아직 손대지 않은 항목만 채운다
        const mineRows = saved.filter((o) => o.atcDivsCd === atcDivsCd);
        setDrafts((prev) => {
          const next = { ...prev };
          mineRows.forEach((o) => {
            if (next[o.anlsOpinTpCd] === undefined) next[o.anlsOpinTpCd] = o.opinCtn ?? '';
          });
          return next;
        });
        // 분석 본문은 사용자가 손댄 항목만 지키고, 나머지는 조회값으로 갱신한다 —
        // AI 초안/최종 리포트가 나중에 도착해도 화면에 반영되게 하기 위함이다.
        setAnalysisDrafts((prev) => {
          const next = { ...prev };
          mineRows.forEach((o) => {
            if (touchedAnalysisRef.current.has(o.anlsOpinTpCd)) return;
            next[o.anlsOpinTpCd] = analysisTextOf(o);
          });
          return next;
        });
        // 이 구분(기계/전기)의 내용이 아직 비어 있고, 최근에 AI 초안을 요청했다면 생성 중으로 본다.
        //
        // ⚠ 이 섹션이 그리는 항목(공통코드 ANLS_OPIN_TP_CD)만 봐야 한다. 기계는 같은 테이블에
        //   UT 항목별 분석결과 9종(CDA/N2/… — MechUtAnalysisSection 담당)이 함께 들어 있고,
        //   그건 리포트 생성 시 규칙 기반으로 즉시 채워진다. 그것까지 세면 AI 초안이 아직
        //   안 왔는데도 "비어 있지 않다"가 되어 진행 표시가 뜨지 않는다.
        //
        //   같은 이유로, 한 구분 안에 규칙 기반과 AI가 섞여 있으면(전기) AI 항목만 봐야 한다.
        const shownCodes = new Set(
          (codeList ?? []).map((c) => c.cmnCd).filter((cd): cd is string => isShown(cd ?? ''))
        );
        const pendingCodes = aiFieldCodes?.length ? new Set(aiFieldCodes) : shownCodes;
        const mine = saved.filter(
          (o) => o.atcDivsCd === atcDivsCd && pendingCodes.has(o.anlsOpinTpCd)
        );
        const isEmpty = mine.length === 0 || mine.every((o) => !o.anlsRltCtn && !o.aiAnsCtn);
        setGenerating(useAiProgress && isEmpty && isAiDraftPending(rptId));

        // 최종 리포트 반영 대기 — BE가 AI_ANS_CTN을 갱신하면 본문이 달라지므로 그걸로 완료를 판단한다.
        // 제한 시간(5분)이 지나면 isFinalReportPending이 false가 되면서 자동으로 멈춘다.
        const sig = aiSignature(saved, atcDivsCd);
        if (isFinalReportPending(rptId)) {
          if (!finalSnapshotRef.current) finalSnapshotRef.current = sig;
          if (sig !== finalSnapshotRef.current) {
            clearFinalReportPending(rptId);
            setFinalPending(false);
          } else {
            setFinalPending(true);
          }
        } else {
          setFinalPending(false);
        }
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [rptId, atcDivsCd, pollCount, useAiProgress]);

  // 초안 생성 중이거나 최종 리포트 반영을 기다리는 동안만 주기적으로 다시 조회한다.
  // 내용이 들어오거나 제한 시간이 지나면 두 플래그가 false가 되면서 폴링이 멈춘다.
  useEffect(() => {
    if (!generating && !finalPending) return;
    const timer = window.setTimeout(() => setPollCount((n) => n + 1), POLL_INTERVAL_MS);
    return () => window.clearTimeout(timer);
  }, [generating, finalPending, pollCount]);

  const byCode = useMemo(() => {
    const map = new Map<string, RptOpinion>();
    opinions.filter((o) => o.atcDivsCd === atcDivsCd).forEach((o) => map.set(o.anlsOpinTpCd, o));
    return map;
  }, [opinions, atcDivsCd]);

  /**
   * 최종 의견 저장.
   *
   * saveRptOpinions는 구분 전체를 지우고 다시 넣는 방식이라(⑤-b), 화면에 안 보이는
   * 행까지 그대로 돌려보내야 한다 — 특히 기계의 UT 항목별 분석결과 9종(⑥,
   * ANLS_RLT_CTN)이 여기 섞여 있어서 빠뜨리면 지워진다. AI 본문(AI_ANS_CTN)도
   * 조회한 값을 그대로 보존해 보낸다.
   *
   * 그래서 화면에 들고 있는 opinions가 아니라 **저장 직전에 다시 읽은 값**을 기준으로
   * 머지한다. ⑥이 자체 저장 버튼을 갖고 있어, 이 화면을 연 뒤 그쪽이 먼저 저장했다면
   * 조회 시점의 opinions는 이미 옛날 값이다.
   */
  const save = async () => {
    if (!rptId || !userId) return;
    setSaving(true);
    try {
      const latest = await getRptOpinions(rptId).catch(() => opinions);
      const savedCodes = new Set(
        latest.filter((o) => o.atcDivsCd === atcDivsCd).map((o) => o.anlsOpinTpCd)
      );

      // ① 이미 저장돼 있던 행 — 이 구분에서 사용자가 편집한 값만 반영한다
      const existing: RptOpinion[] = latest.map((o) => {
        if (o.atcDivsCd !== atcDivsCd) return o;
        const next = { ...o };
        if (showOpinion && drafts[o.anlsOpinTpCd] !== undefined) {
          next.opinCtn = drafts[o.anlsOpinTpCd];
        }
        /*
          ⚠ **사용자가 실제로 손댄 항목만** 내보낸다. draft가 비어 있지 않은지로 판단하면
          안 된다 — 조회 시점에 내용이 없던 항목은 draft가 ''로 들어오는데, 그 뒤 리포트
          생성기(규칙 기반)나 AI가 DB를 채워도 화면은 여전히 ''을 들고 있어서 저장 순간
          자동 생성 결과가 지워진다. 전기 탭은 폴링 갱신도 없어(useAiProgress=false)
          한 번 빈 값으로 로드되면 갱신 기회조차 없다.

          손대지 않은 항목은 latest(저장 직전 재조회값)를 그대로 되돌려 보낸다.
          내용을 일부러 비워 저장하는 경우는 그대로 반영된다 — 지우는 순간 onChange가
          touchedAnalysisRef에 넣기 때문이다.
        */
        if (editableAnalysis && touchedAnalysisRef.current.has(o.anlsOpinTpCd)) {
          next[fieldOf(o.anlsOpinTpCd)] = analysisDrafts[o.anlsOpinTpCd] ?? '';
        }
        return next;
      });
      // ② 저장된 적 없는 항목에 내용을 새로 쓴 경우 행을 만들어 붙인다
      //    (이 인스턴스가 그리는 항목만 — 섹션을 나눴을 때 남의 항목을 만들지 않는다)
      const added: RptOpinion[] = codes
        .filter((c) => {
          if (!c.cmnCd || !isShown(c.cmnCd) || savedCodes.has(c.cmnCd)) return false;
          const hasOpin = showOpinion && !!drafts[c.cmnCd];
          const hasAnalysis = editableAnalysis && !!analysisDrafts[c.cmnCd];
          return hasOpin || hasAnalysis;
        })
        .map((c, idx) => {
          const cd = c.cmnCd as string;
          const text = editableAnalysis ? analysisDrafts[cd] ?? '' : '';
          const field = fieldOf(cd);
          return {
            rptId,
            sortOrd: existing.length + idx + 1,
            atcDivsCd,
            anlsOpinTpCd: cd,
            anlsRltCtn: field === 'anlsRltCtn' ? text : '',
            aiAnsCtn: field === 'aiAnsCtn' ? text : '',
            opinCtn: showOpinion ? drafts[cd] ?? '' : '',
          };
        });

      const nextRows = [...existing, ...added];
      await saveRptOpinions(rptId, nextRows, userId);
      setOpinions(nextRows);
      setSavedMsg('저장됨');
      window.setTimeout(() => setSavedMsg(''), 2000);

      // 사용자 의견이 확정된 시점에 최종 리포트 AI를 호출한다.
      // 응답은 기다리지 않는다 — BE가 OPIN_M.AI_ANS_CTN에 반영하면 아래 폴링이 주워온다.
      //
      // ⚠ 검토 의견 칸이 없는 화면(showOpinion=false)에서는 호출하지 않는다.
      //   ① 반영할 사용자 의견(user_content)이 빈 값으로 나가고,
      //   ② 그런 화면은 분석 본문을 사용자가 직접 고치는 구조(editableAnalysis)라
      //      BE가 AI_ANS_CTN을 다시 쓰면 방금 저장한 수정 내용이 지워진다.
      if (showOpinion) {
        finalSnapshotRef.current = aiSignature(nextRows, atcDivsCd);
        requestFinalReport(rptId, rptNm);
        setFinalPending(true);
      }
    } catch (e) {
      window.alert('종합의견 저장 오류: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  // 탭 하단 저장을 버튼 하나로 모을 때 부모가 호출한다 (hideSaveButton과 함께 쓴다).
  // 조기 반환(loading/error)보다 위에 있어야 훅 순서가 깨지지 않는다.
  useImperativeHandle(ref, () => ({ save }));

  if (loading) return <p style={mutedStyle}>분석 항목을 불러오는 중…</p>;
  if (error) {
    return <p style={errorStyle}>분석 항목 조회 오류: {error}</p>;
  }
  /** 이 인스턴스가 그릴 항목 목록 — 순서는 공통코드(SORT_ORD)를 따른다 */
  const shownCodes = codes.filter((code) => isShown(code.cmnCd ?? ''));
  if (shownCodes.length === 0) return <p style={mutedStyle}>등록된 분석 항목이 없습니다.</p>;

  // AI가 만드는 중이면 빈 칸 대신 진행 상태를 보여준다
  const placeholder = generating ? 'AI가 분석 중입니다…' : '아직 생성된 내용이 없습니다.';

  return (
    <>
      {(generating || finalPending) && (
        <>
          <style>{SPINNER_KEYFRAMES}</style>
          <p style={{ ...mutedStyle, margin: '0 0 10px' }}>
            <span style={spinnerStyle} />
            {generating
              ? 'AI 분석 중입니다. 완료되면 자동으로 표시됩니다.'
              : '검토 의견을 반영해 AI가 최종 리포트를 작성 중입니다. 완료되면 자동으로 갱신됩니다.'}
          </p>
        </>
      )}
      {shownCodes.map((code) => {
        const cd = code.cmnCd as string;
        const hit = byCode.get(cd);
        // 분석 본문 — analysisField 쪽을 우선하고 비어 있으면 반대쪽으로 넘어간다
        const aiText = analysisTextOf(hit);
        return (
          <div key={cd} style={itemStyle}>
            <div style={itemTitleRowStyle}>
              <p style={itemTitleStyle}>{itemTitles?.[cd] || code.cmnCdNm || cd}</p>
              {renderItemAction?.(cd)}
            </div>
            <div style={showOpinion ? rowStyle : singleColRowStyle}>
              <div style={colStyle}>
                {/*
                    빈 문자열이면 라벨 줄 자체를 없앤다 (제목에 이미 드러나는 경우).
                    - analysisLabel='' : 기계 탭 — 제목이 '종합분석(AI)'이라 라벨이 중복
                    - labelOf가 ''     : AI 항목 — 위와 같은 이유
                    - A-0001           : 아래 두 표가 각자 제목을 달고 있어 라벨이 겹친다
                  */}
                {labelOf(cd) && cd !== BRKE_MARGIN_CD && <p style={labelStyle}>{labelOf(cd)}</p>}
                {cd === BRKE_MARGIN_CD ? (
                  /*
                      [26.09.12] 차단기 여유율 분석(A-0001) 표로 별도 노출
                      [26.09.23] Machine별 표를 팝업(MachineMarginModal)에서 꺼내 오른쪽에 나란히 둔다
                    */
                  <div style={marginTableRowStyle}>
                    <div style={marginTableColStyle}>
                      <p style={marginTableCaptionStyle}>차단기 여유율 분석 결과</p>
                      <CustomGrid
                        id="utBrkeMarginGrid"
                        layoutDefinition={BRKE_MARGIN_LAYOUT}
                        rowData={brkeMarginRatios}
                        height={MARGIN_GRID_H}
                        itemFormatter={brkeMarginItemFormatter}
                        isFilter={false}
                        isSelector={false}
                        isReadOnly
                        allowPinning={false}
                      />
                    </div>
                    <div style={marginTableColStyle}>
                      <p style={marginTableCaptionStyle}>Machine 별 차단기 여유율</p>
                      <CustomGrid
                        id="utMachMarginGrid"
                        layoutDefinition={MACH_MARGIN_LAYOUT}
                        rowData={machMarginRatios}
                        height={MARGIN_GRID_H}
                        isFilter={false}
                        isSelector={false}
                        isReadOnly
                        allowPinning={false}
                      />
                    </div>
                  </div>
                ) : editableAnalysis ? (
                  <textarea
                    style={textareaStyle}
                    rows={5}
                    value={analysisDrafts[cd] ?? ''}
                    placeholder={placeholder}
                    onChange={(e) => {
                      touchedAnalysisRef.current.add(cd);
                      setAnalysisDrafts((prev) => ({ ...prev, [cd]: e.target.value }));
                    }}
                  />
                ) : (
                  <div style={aiText ? boxStyle : emptyBoxStyle}>{aiText || placeholder}</div>
                )}
              </div>
              {showOpinion && (
                <div style={colStyle}>
                  <p style={labelStyle}>검토 의견</p>
                  <textarea
                    style={textareaStyle}
                    rows={3}
                    value={drafts[cd] ?? ''}
                    placeholder="검토 의견을 입력하세요"
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [cd]: e.target.value }))}
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!hideSaveButton && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <button
            type="button"
            style={saving || !userId ? saveButtonDisabledStyle : saveButtonStyle}
            onClick={save}
            disabled={saving || !userId}
          >
            {saving ? '저장 중…' : '의견 저장'}
          </button>
          {savedMsg && <span style={mutedStyle}>{savedMsg}</span>}
        </div>
      )}
    </>
  );
});

export default AnalysisOpinionSection;
